/** Mirrors the backend envelope: {"error": {"code", "message", "fields"}}. */
export interface ApiErrorBody {
  code: string;
  message: string;
  fields: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.fields = body.fields ?? {};
  }

  /** First message for a field (DRF sends lists of strings, nested for objects). */
  fieldMessage(field: string): string | undefined {
    return firstString(this.fields[field]);
  }
}

function firstString(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return firstString(value[0]);
  if (value && typeof value === "object") return firstString(Object.values(value)[0]);
  return undefined;
}

export function isApiErrorBody(value: unknown): value is { error: ApiErrorBody } {
  if (!value || typeof value !== "object" || !("error" in value)) return false;
  const err = (value as { error: unknown }).error;
  return !!err && typeof err === "object" && "code" in err && "message" in err;
}

export function toApiError(status: number, body: unknown): ApiError {
  if (isApiErrorBody(body)) return new ApiError(status, body.error);
  return new ApiError(status, {
    code: status === 0 ? "NETWORK_ERROR" : "SERVER_ERROR",
    message: status === 0 ? "Can't reach RealityStore. Check your connection." : "Something went wrong. Please try again.",
    fields: {},
  });
}

/** Human message for any thrown value (for toasts). */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong. Please try again.";
}
